/**
 * API Search Service - Transport layer for Meilisearch proxy
 *
 * Pure transport layer with no UI logic or business logic.
 * Implements the SearchService interface to communicate with the local proxy server.
 */

import { SearchService, SearchOptions, SearchResponse, SearchResult } from "../types";
import { ApiSearchServiceConfig, MeilisearchSearchRequest, MeilisearchSearchResponse, ProxyErrorResponse } from "../types/api";
import { isBitrixMode, buildBitrixApiUrl } from "../utils/bitrixApiUrl";

// Default configuration
const DEFAULT_CONFIG: ApiSearchServiceConfig = {
	baseUrl: import.meta.env.VITE_API_BASE_URL || "/local/modules/live_search/api",
	timeout: 10000, // 10 seconds
	headers: {
		"Content-Type": "application/json",
	},
};

/**
 * API Search Service implementation
 *
 * This service is a pure transport layer that:
 * - Sends POST requests to /api/search on the local proxy
 * - Handles errors and timeouts
 * - Returns minimally transformed responses (hits as-is)
 * - Contains no UI state management or business logic
 */
export class ApiSearchService implements SearchService {
	private config: ApiSearchServiceConfig;
	private abortController: AbortController | null = null;

	constructor(config: Partial<ApiSearchServiceConfig> = {}) {
		this.config = { ...DEFAULT_CONFIG, ...config };
	}

	/**
	 * Initialize the service
	 * Can be used for pre-flight checks or authentication if needed
	 */
	async initialize(config: Record<string, unknown>): Promise<void> {
		// Merge any additional config
		this.config = { ...this.config, ...config };
	}

	/**
	 * Perform a search query
	 *
	 * Sends a POST request to /api/search with the query and options.
	 * Returns minimally transformed response (hits as-is).
	 *
	 * @param query - Search query string
	 * @param options - Search options (limit, offset, filters, etc.)
	 * @returns SearchResponse with results
	 */
	async search(query: string, options: SearchOptions = {}): Promise<SearchResponse> {
		// Cancel any ongoing request
		this.cancelCurrentRequest();

		// Create new abort controller for this request
		this.abortController = new AbortController();

		try {
			// Prepare request body for Meilisearch
			const requestBody: MeilisearchSearchRequest = {
				q: query,
				limit: options.limit || 20,
				offset: options.offset || 0,
				...(options.filters && { filter: options.filters }),
				...(options.sortBy && { sort: [`${options.sortBy}:${options.sortOrder || "asc"}`] }),
			};

			// Prepare fetch options
			const timeout = this.config.timeout || DEFAULT_CONFIG.timeout;
			const controller = this.abortController;

			// Set up timeout promise
			const timeoutPromise = new Promise<never>((_, reject) => {
				setTimeout(() => {
					controller.abort();
					reject(new Error(`Request timeout after ${timeout}ms`));
				}, timeout);
			});

			// Prepare fetch request
			const url = isBitrixMode() ? buildBitrixApiUrl("/api/search") : `${this.config.baseUrl}/api/search`;
			const fetchPromise = fetch(url, {
				method: "POST",
				headers: {
					...this.config.headers,
					Accept: "application/json",
				},
				body: JSON.stringify(requestBody),
				signal: controller.signal,
			});

			// Race between fetch and timeout
			const response = await Promise.race([fetchPromise, timeoutPromise]);

			if (!response.ok) {
				let errorMessage = `HTTP error ${response.status}`;
				try {
					const errorData: ProxyErrorResponse = await response.json();
					errorMessage = errorData.error || errorData.message || errorMessage;
				} catch {
					// Ignore JSON parsing errors
				}
				throw new Error(errorMessage);
			}

			// Parse the raw Meilisearch response
			const rawResponse: MeilisearchSearchResponse = await response.json();

			// Minimal transformation: convert hits to results (hits as-is)
			// For now, we'll map hits to SearchResult with minimal structure
			// As per requirements: results can be formed minimally (hits as is)
			const results: SearchResult[] = rawResponse.hits.map((hit: any, index: number) => ({
				id: hit.id || index,
				title: hit.title || hit.name || `Result ${index + 1}`,
				description: hit.description || "",
				category: hit.category || hit.type || "unknown",
				price: hit.price ? String(hit.price) : "",
				image: hit.image || hit.imageUrl || undefined,
				icon: hit.icon || undefined,
				metadata: hit, // Preserve all original data in metadata
			}));

			// Return the transformed response
			return {
				results,
				total: rawResponse.estimatedTotalHits || rawResponse.totalHits || 0,
				took: rawResponse.processingTimeMs,
				facets: rawResponse.facets ? (rawResponse.facets as Record<string, unknown>) : undefined,
				provider: "api",
			};
		} catch (error) {
			// Handle abort errors separately
			if (error instanceof Error && error.name === "AbortError") {
				throw new Error("Search request was cancelled");
			}

			// Re-throw the error for the hook to handle
			throw error;
		} finally {
			// Clear the abort controller
			this.abortController = null;
		}
	}

	/**
	 * Cancel the current search request if any
	 */
	private cancelCurrentRequest(): void {
		if (this.abortController) {
			this.abortController.abort();
			this.abortController = null;
		}
	}

	/**
	 * Get search suggestions/autocomplete
	 *
	 * Not implemented in this phase - returns empty array
	 */
	async suggest(_query: string, _options: SearchOptions = {}): Promise<string[]> {
		return [];
	}

	/**
	 * Get filters/facets available for search
	 *
	 * Not implemented in this phase - returns empty object
	 */
	async getFilters(): Promise<Record<string, unknown>> {
		return {};
	}

	/**
	 * Health check for the search service
	 *
	 * Checks if the proxy server is reachable
	 */
	async healthCheck(): Promise<{ healthy: boolean; message?: string }> {
		try {
			const url = isBitrixMode() ? buildBitrixApiUrl("/api/health") : `${this.config.baseUrl}/health`;
			const response = await fetch(url, {
				method: "GET",
				signal: AbortSignal.timeout(3000),
			});

			const isHealthy = response.ok;
			return {
				healthy: isHealthy,
				message: isHealthy ? "Proxy server is reachable" : `Proxy server returned ${response.status}`,
			};
		} catch (error) {
			return {
				healthy: false,
				message: error instanceof Error ? error.message : "Unknown error checking proxy health",
			};
		}
	}

	/**
	 * Cancel any ongoing search request
	 * Public method for the hook to call
	 */
	cancel(): void {
		this.cancelCurrentRequest();
	}
}
